import {
  Box,
  Checkbox,
  IconButton,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PropTypes from "prop-types";

import TableWrapper from "components/TableWrapper";
import Loader from "components/Loader";
import ButtonV1 from "components/ButtonV1";
import { WEEK_DAYS, WEEKLY_SCHEDULE_COLUMNS } from "constants/eddConfig";
import {
  formatDuration,
  getDurationInMinutes,
  getWeeklyTotalHours,
  isSameScheduleRow as isSameRow,
  validateScheduleRow,
} from "utils/eddUtils";

const WeeklySchedule = ({
  schedule,
  savedSchedule,
  isLoading,
  isSaving,
  onRowChange,
  onRowReset,
  onDiscard,
  onSave,
}) => {
  const getRow = (rows, day) => rows.find((r) => r.day === day) || {};
  const isDirty = schedule.some(
    (row) => !isSameRow(row, getRow(savedSchedule, row.day)),
  );
  const hasErrors = schedule.some((row) => validateScheduleRow(row));
  const weeklyTotal = getWeeklyTotalHours(schedule);

  const tableHeader = WEEKLY_SCHEDULE_COLUMNS.map((col) => (
    <TableCell
      key={col.field}
      sx={{ whiteSpace: "nowrap" }}
      align={col.field === "action" ? "right" : "left"}
    >
      {col.headerName}
    </TableCell>
  ));

  const renderTimeInput = (row, field, rowError) => (
    <TextField
      type="time"
      size="small"
      value={row[field] || ""}
      disabled={!row.is_active || isSaving}
      error={Boolean(rowError) && field === "end_time"}
      onChange={(e) => onRowChange(row.day, field, e.target.value)}
      inputProps={{ "aria-label": `${row.day} ${field}`, step: 300 }}
      sx={{ width: 140 }}
    />
  );

  const tableBody = isLoading
    ? [
        <TableRow key="loading">
          <TableCell
            colSpan={WEEKLY_SCHEDULE_COLUMNS.length}
            align="center"
            sx={{ py: 4 }}
          >
            <Loader size={28} sx={{ marginTop: 0 }} />
          </TableCell>
        </TableRow>,
      ]
    : [
        ...WEEK_DAYS.map(({ key, label }) => {
          const row = getRow(schedule, key);
          const rowError = validateScheduleRow(row);
          const isRowDirty = !isSameRow(row, getRow(savedSchedule, key));
          const duration = row.is_active
            ? getDurationInMinutes(row.start_time, row.end_time)
            : 0;

          return (
            <TableRow key={key} data-testid={`schedule-row-${key}`}>
              <TableCell sx={{ fontWeight: 600 }}>{label}</TableCell>
              <TableCell>
                {renderTimeInput(row, "start_time", rowError)}
              </TableCell>
              <TableCell>
                {renderTimeInput(row, "end_time", rowError)}
                {rowError && (
                  <Typography
                    fontSize={12}
                    color="error"
                    sx={{ display: "block", mt: 1 }}
                  >
                    {rowError}
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                {formatDuration(duration)}
              </TableCell>
              <TableCell>
                <Checkbox
                  size="small"
                  checked={Boolean(row.is_active)}
                  disabled={isSaving}
                  onChange={(e) =>
                    onRowChange(key, "is_active", e.target.checked)
                  }
                  inputProps={{ "aria-label": `${key} active` }}
                />
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Reset">
                  <span>
                    <IconButton
                      size="small"
                      aria-label={`reset ${key}`}
                      disabled={!isRowDirty || isSaving}
                      onClick={() => onRowReset(key)}
                    >
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          );
        }),
        <TableRow key="total">
          <TableCell sx={{ fontWeight: 600 }}>Weekly total</TableCell>
          <TableCell />
          <TableCell />
          <TableCell data-testid="weekly-total" sx={{ fontWeight: 600 }}>
            {`${weeklyTotal.toFixed(1)} h`}
          </TableCell>
          <TableCell />
          <TableCell />
        </TableRow>,
      ];

  return (
    <>
      <TableWrapper tableHeader={tableHeader} tableBody={tableBody} />
      <Box display="flex" justifyContent="flex-end" gap={4} mt={4}>
        <ButtonV1
          title="Discard"
          variant="outlined"
          size="small"
          onClick={onDiscard}
          disabled={!isDirty || isSaving}
        />
        <ButtonV1
          title={isSaving ? "Saving..." : "Save schedule"}
          size="small"
          onClick={onSave}
          loader={isSaving}
          disabled={!isDirty || hasErrors || isSaving}
        />
      </Box>
    </>
  );
};

WeeklySchedule.propTypes = {
  schedule: PropTypes.arrayOf(PropTypes.object).isRequired,
  savedSchedule: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool,
  isSaving: PropTypes.bool,
  onRowChange: PropTypes.func.isRequired,
  onRowReset: PropTypes.func.isRequired,
  onDiscard: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default WeeklySchedule;
